import { enumDeviceDiagnosticsType } from '@ingenico/api-sdk-device-management-private-v2';
import { SectionPageHeader, useSetHeaderMobileInformation } from '@ingenico/react-section-utils';
import { CoreContainer } from '@ingenico/react-ui/core-container';
import { CoreStatus, StatusSize } from '@ingenico/react-ui/core-status';
import { LinearProgress, useMediaQuery } from '@mui/material';
import classNames from 'classnames';

import type { ReactElement } from 'react';
import { useEffect, useMemo } from 'react';

import { Banner } from '@ingenico/react-ui/banner';
import { DATAGRID_ROWS_PER_PAGE_OPTIONS, DataGridCore, ListViewContainer } from '@ingenico/react-ui/datagrid';
import { isEmpty, toString } from 'lodash-es';
import { DeviceDiagnosticsDetailsDrawer } from '../../components/device-diagnostic-details-drawer/DeviceDiagnosticsDetailsDrawer.ctrl';
import styles from './DeviceDiagnosticPage.module.scss';
import { useDeviceDiagnosticPage } from './useDeviceDiagnosticPage.vm';

export function DeviceDiagnosticPage(): ReactElement {
  const { data, date, isError, isLoading, t, columns, gridProps, noRowsProps, drawer } = useDeviceDiagnosticPage();

  const isMobile = useMediaQuery('(max-width: 766px)');

  const setHeaderMobileInformation = useSetHeaderMobileInformation();
  const statusType = useMemo(
    () => (data?.type === enumDeviceDiagnosticsType.AUTOMATIC_DIAGNOSTICS ? 'automatic' : 'manual'),
    [data],
  );

  useEffect(() => {
    setHeaderMobileInformation({
      title: toString(t('deviceDiagnostics.title')),
      subtitle: date,
      status: statusType,
    });
  }, [data, date, setHeaderMobileInformation, statusType, t]);

  return (
    <>
      {!isMobile && (
        <SectionPageHeader
          isWhiteBackground
          subHeader={
            <div className={classNames(styles.statusContainer)}>
              <CoreStatus
                data-testid="logical-device-diagnostic-status-chip"
                size={StatusSize.Medium}
                label={toString(t(`deviceDiagnostics.status.${statusType}`))}
                status={'info'}
              />
            </div>
          }
          subtitle={date}
          title={t('deviceDiagnostics.title')}
        />
      )}

      {isError && <Banner message={t('error.default')} />}
      {isLoading || (isEmpty(data) && <LinearProgress />)}

      {gridProps?.shouldDisplayGrid && (
        <CoreContainer>
          <ListViewContainer>
            <DataGridCore
              apiRef={gridProps.datagridRef}
              classes={{ row: styles.row }}
              columnBufferPx={5}
              columns={columns}
              getRowId={gridProps.getRowId}
              headerHeightPixels={395}
              pagination={false}
              loading={isLoading}
              noRowsProps={noRowsProps}
              onRowClick={gridProps.handleRowClick}
              rowCount={gridProps.total}
              rows={data?.testResults || []}
              pageSizeOptions={DATAGRID_ROWS_PER_PAGE_OPTIONS}
            />
          </ListViewContainer>
        </CoreContainer>
      )}

      <DeviceDiagnosticsDetailsDrawer
        isOpen={drawer.isOpen}
        onCloseDrawer={drawer.handleCloseDrawer}
        testResultDetails={drawer.testResultDetails}
        isLoading={isLoading}
      ></DeviceDiagnosticsDetailsDrawer>
    </>
  );
}
